import { createRoot } from "react-dom/client";
import "../../ui/kit/styles.ts";
import type { InputRouter } from "../../ui/input/router.ts";
import { IconsProvider } from "../icons/context.tsx";
import type { IconSource } from "../icons/page-icons.ts";
import { DialogueView, type Speakers } from "./panel.tsx";
import type { DialogueControl } from "./store.ts";

/**
 * Mounts the dialogue window (ADR-136 decision 5.6) in its host in the overlay root, a React root of its own that
 * follows the dialogue store. The host comes after the reading panel's, so the loading shell and the game menu cover
 * it; the page keeps the host inert until the world is revealed.
 *
 * @param host The page's `#dialogue` element.
 * @param store The store the window reads and acts on.
 * @param input The page's input router, which the window opens its layer on.
 * @param speakers Names and draws a speaker by its index in the NPC look table, read as each node shows.
 * @param icons The page's icons, whose atlas draws the choices' marks.
 * @example
 * mountDialogue(document.getElementById("dialogue") as HTMLElement, dialogue, input, { name: (speaker) => looks[speaker]?.name ?? "", portrait: () => null });
 */
export function mountDialogue(host: HTMLElement, store: DialogueControl, input: Pick<InputRouter, "open">, speakers: Speakers, icons: IconSource): void {
  createRoot(host).render(
    <IconsProvider source={icons}>
      <DialogueView store={store} input={input} speakers={speakers} />
    </IconsProvider>,
  );
}
